import React from "react";

import deepEqual from "deep-equal";
import _ from "lodash";

import { MonthIntervalPicker } from "@components/month-interval-picker";
import { pickConfig } from "@components/reports/breakdown-selector";
import {
  ControlCenterWrapper,
  ControlWrapper,
} from "@components/reports/control-center-utilities";
import { useLogger } from "@components/v2/analytics";
import {
  type Breakdown,
  type BreakdownConfig,
  type MonthInterval,
} from "@north-beam/nb-common";
import {
  parseBreakdownQueryParam,
  parseMonthInterval,
} from "@north-beam/nb-common";
import { PrimaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";

export interface CustomerReportParams {
  readonly breakdown: Breakdown;
  readonly firstAcquiredMonthRange: MonthInterval;
}

interface CustomerReportState {
  breakdown?: Breakdown; // not actually readonly
  readonly firstAcquiredMonthRange: MonthInterval;
}

interface UpdateBreakdown {
  type: "UpdateBreakdown";
  value: Breakdown | undefined;
}

interface UpdateFirstAcquiredMonthRange {
  type: "UpdateFirstAcquiredMonthRange";
  value: MonthInterval;
}

interface RefreshState {
  type: "RefreshState";
  value: CustomerReportState;
}

type ReportAction =
  | UpdateBreakdown
  | UpdateFirstAcquiredMonthRange
  | RefreshState;

function ReportStateReducer(
  state: CustomerReportState,
  action: ReportAction,
): CustomerReportState {
  switch (action.type) {
    case "UpdateFirstAcquiredMonthRange":
      return satisfyConstraints({
        ...state,
        firstAcquiredMonthRange: action.value,
      });
    case "UpdateBreakdown":
      return { ...state, breakdown: action.value };
    case "RefreshState":
      return action.value;
  }
}

function satisfyConstraints(state: CustomerReportState): CustomerReportState {
  const rv = { ...state };
  const mi = state.firstAcquiredMonthRange;
  const clip = (bd: Breakdown) => clipMonthBreakdown(bd, mi);
  if (rv.breakdown) {
    rv.breakdown = clip(rv.breakdown);
  }
  return state;
}

function clipMonthBreakdown(
  breakdown: Breakdown,
  mi: MonthInterval,
): Breakdown {
  if (breakdown.key !== "month") {
    return breakdown;
  }

  const { startMonth, endMonth } = mi;
  return {
    ...breakdown,
    values: breakdown.values.filter((v) => v >= startMonth && v <= endMonth),
  };
}

function clipMonthBreakdownConfig(
  config: BreakdownConfig,
  mi: MonthInterval,
): BreakdownConfig {
  if (config.type !== "month") {
    return config;
  }

  const { startMonth, endMonth } = mi;
  return {
    ...config,
    minMonth: _.max([startMonth, config.minMonth])!,
    maxMonth: _.min([endMonth, config.maxMonth])!,
  };
}

export interface CustomerControlCenterProps {
  onReportStateUpdated: (state: CustomerReportParams) => void;
  disabled: boolean;
  initialState: CustomerReportState;
  firstAcquiredBounds: MonthInterval;
  breakdownConfigs: BreakdownConfig[];
}

export function CustomerControlCenter({
  initialState,
  breakdownConfigs: originalBreakdownConfigs,
  disabled,
  onReportStateUpdated,
  firstAcquiredBounds,
}: CustomerControlCenterProps) {
  const [proposedState, dispatch] = React.useReducer(
    ReportStateReducer,
    initialState,
  );

  const logger = useLogger({ container: "controls" });

  React.useEffect(
    () => dispatch({ type: "RefreshState", value: initialState }),
    [initialState],
  );

  const updateBreakdown = (value: BreakdownConfig | null) => {
    if (value) {
      dispatch({
        type: "UpdateBreakdown",
        value: { key: value.key, values: [] },
      });

      logger.event("customer-ltv-page-select-groupby", "select", {
        eventDetails: {
          groupBy: value.key,
        },
      });
    }
  };

  const { firstAcquiredMonthRange, breakdown } = proposedState;

  const breakdownConfigs = React.useMemo(() => {
    return originalBreakdownConfigs.map((v) =>
      clipMonthBreakdownConfig(v, firstAcquiredMonthRange),
    );
  }, [originalBreakdownConfigs, firstAcquiredMonthRange]);

  const actualAvailableBreakdowns = breakdownConfigs.map(({ name, key }) => ({
    label: name,
    value: pickConfig(key, originalBreakdownConfigs),
  }));

  const submit = isReadyForSubmit(proposedState)
    ? () => {
        onReportStateUpdated(proposedState);
        logger.event("customer-ltv-page-select-update-report", "select", {
          eventDetails: {
            groupBy: proposedState.breakdown.key,
            dateRange: {
              startDate: proposedState.firstAcquiredMonthRange.startMonth,
              endDate: proposedState.firstAcquiredMonthRange.endMonth,
            },
          },
        });
      }
    : // eslint-disable-next-line @typescript-eslint/no-empty-function
      () => {};

  const blockSubmitButton =
    disabled ||
    deepEqual(initialState, proposedState) ||
    !isReadyForSubmit(proposedState);

  return (
    <ControlCenterWrapper>
      <ControlWrapper title="Customers first acquired between...">
        <MonthIntervalPicker
          disabled={disabled}
          bounds={firstAcquiredBounds}
          value={firstAcquiredMonthRange}
          onUpdate={(mi) => {
            dispatch({
              type: "UpdateFirstAcquiredMonthRange",
              value: mi,
            });
            logger.event("customer-ltv-page-select-date-range", "select", {
              eventDetails: {
                dateRange: {
                  startDate: mi.startMonth,
                  endDate: mi.endMonth,
                },
              },
            });
          }}
        />
      </ControlWrapper>
      <ControlWrapper title="GROUP BY">
        <Select
          isDisabled={disabled}
          onChange={updateBreakdown}
          value={
            (breakdown &&
              pickConfig(breakdown.key, originalBreakdownConfigs)) ||
            null
          }
          options={actualAvailableBreakdowns}
          placeholder="Add breakdown..."
          icon={<i className="fa-regular fa-sharp fa-chart-pie" />}
        />
      </ControlWrapper>
      <ControlWrapper>
        <PrimaryButton
          size="md"
          isDisabled={blockSubmitButton}
          onClick={submit}
          fullWidth
        >
          UPDATE REPORT
        </PrimaryButton>
      </ControlWrapper>
    </ControlCenterWrapper>
  );
}

export function isReadyForSubmit(
  state: CustomerReportState,
): state is CustomerReportParams {
  return !!state.breakdown;
}

export function makeReportStateQuery(state: CustomerReportState) {
  const search = new URLSearchParams();
  search.set(
    "firstAcquiredMonthRange",
    JSON.stringify(state.firstAcquiredMonthRange),
  );
  if (state.breakdown) {
    search.set("breakdown", JSON.stringify(state.breakdown));
  }
  return search;
}

export function parseReportState(
  query: string,
  firstAcquiredBounds: MonthInterval,
): CustomerReportState {
  const params = new URLSearchParams(query);
  const firstAcquiredMonthRange =
    parseMonthInterval(params.get("firstAcquiredMonthRange")) ??
    firstAcquiredBounds;
  const breakdown =
    parseBreakdownQueryParam(params.get("breakdown")) ?? undefined;

  return satisfyConstraints({
    firstAcquiredMonthRange,
    breakdown,
  });
}
